import { useState } from 'react';
import { Calendar, CheckCircle2, CreditCard, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useCancelSubscriptionMutation,
  useGetSubscriptionStatusQuery,
} from '@/apis/extended/subscriptionApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export function SubscriptionManagement() {
  const { data: subscriptionStatus, isLoading } =
    useGetSubscriptionStatusQuery();
  const [cancelSubscription, { isLoading: isCancelling }] =
    useCancelSubscriptionMutation();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCancel = async () => {
    if (
      !confirm(
        'Are you sure you want to cancel your subscription? You will lose access to investor features at the end of your billing period.'
      )
    ) {
      return;
    }

    try {
      setIsProcessing(true);
      await cancelSubscription().unwrap();
      toast.success(
        'Subscription cancelled. Your subscription will remain active until the end of the billing period.'
      );
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const isInvestor = subscriptionStatus?.userType === 'investor';
  const isAdmin = subscriptionStatus?.userType === 'admin';
  const isActive = subscriptionStatus?.status === 'active';
  const isPending = subscriptionStatus?.status === 'pending';
  const isDenied = subscriptionStatus?.status === 'denied';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription</CardTitle>
        <CardDescription>Manage your subscription and access</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Plan */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Current Plan</span>
            <Badge variant={isInvestor ? 'default' : 'secondary'}>
              {isInvestor ? 'Investor' : isAdmin ? 'Admin' : 'Free'}
            </Badge>
          </div>

          {isPending && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
              <p className="font-medium text-amber-900">
                Payment request pending
              </p>
              <p className="text-amber-800 mt-1">
                We recorded your Investor checkout request and emailed you a
                confirmation. Access unlocks after approval — not on this toast
                alone.
              </p>
            </div>
          )}

          {isDenied && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              Your last Investor request was not approved. You can request again
              from Pricing.
            </div>
          )}

          {isInvestor && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <div className="flex items-center gap-2">
                  {isActive ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span className="text-green-500">Active</span>
                    </>
                  ) : (
                    <>
                      <Calendar className="h-4 w-4 text-red-500" />
                      <span className="text-red-500">Cancelled</span>
                    </>
                  )}
                </div>
              </div>

              {subscriptionStatus?.startDate && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Start Date</span>
                  <span>
                    {new Date(
                      subscriptionStatus.startDate
                    ).toLocaleDateString()}
                  </span>
                </div>
              )}

              {subscriptionStatus?.endDate && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {isActive ? 'Renews' : 'Expires'}
                  </span>
                  <span>
                    {new Date(subscriptionStatus.endDate).toLocaleDateString()}
                  </span>
                </div>
              )}

              {subscriptionStatus?.subscription?.amount && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Monthly Cost</span>
                  <div className="flex items-center gap-1">
                    <span className="text-lg font-semibold">
                      ${subscriptionStatus.subscription.amount}
                    </span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                </div>
              )}

              {subscriptionStatus?.stripeSubscriptionId && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <CreditCard className="h-4 w-4" />
                    Subscription ID
                  </span>
                  <span className="font-mono text-xs">
                    {subscriptionStatus.stripeSubscriptionId.substring(0, 20)}
                    ...
                  </span>
                </div>
              )}
            </div>
          )}

          {!isInvestor && !isAdmin && !isPending && (
            <div className="rounded-lg border bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground mb-2">
                Upgrade to access investor products:
              </p>
              <ul className="text-sm space-y-1">
                <li>• Bridge loans</li>
                <li>• DSCR loans</li>
                <li>• Commercial loans</li>
                <li>• Fix and Flip loans</li>
              </ul>
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex gap-2">
        {isInvestor && isActive && (
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={isCancelling || isProcessing}
            className="flex-1"
          >
            {isCancelling || isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cancelling...
              </>
            ) : (
              'Cancel Subscription'
            )}
          </Button>
        )}

        {!isInvestor && !isAdmin && !isPending && (
          <Button onClick={handleUpgrade} className="flex-1">
            Upgrade to Investor
          </Button>
        )}

        {isPending && (
          <Button variant="outline" disabled className="flex-1">
            Awaiting approval
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
